import { Component } from 'react'
import type { ErrorInfo, ReactNode } from 'react'
import { Button, Result, Space } from 'antd'
import { RefreshCw } from 'lucide-react'

interface ErrorBoundaryProps {
  children: ReactNode
}

interface ErrorBoundaryState {
  error: Error | null
}

const CHUNK_RELOAD_KEY = 'chunk_error_reload_at'
const CHUNK_RELOAD_INTERVAL_MS = 30_000

// Vite 生产构建发版后，浏览器持有旧 index.html 引用的旧 chunk 会 404，
// React.lazy 动态导入失败会把整棵组件树打穿，必须兜底。
const CHUNK_ERROR_PATTERN =
  /dynamically imported module|Importing a module script failed|error loading dynamically imported|ChunkLoadError|Loading CSS chunk/i

function isChunkLoadError(error: Error | null): boolean {
  if (!error) return false
  return error.name === 'ChunkLoadError' || CHUNK_ERROR_PATTERN.test(error.message)
}

/**
 * 渲染错误兜底边界：捕获子树渲染错误与懒加载 chunk 加载失败，
 * 展示可恢复的错误界面，避免整页白屏。
 */
export default class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { error: null }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('页面渲染出错:', error, info.componentStack)
    if (isChunkLoadError(error)) {
      const lastReloadAt = Number(sessionStorage.getItem(CHUNK_RELOAD_KEY) ?? 0)
      // 30 秒内只自动刷新一次，避免资源持续 404 时陷入刷新死循环
      if (Date.now() - lastReloadAt > CHUNK_RELOAD_INTERVAL_MS) {
        sessionStorage.setItem(CHUNK_RELOAD_KEY, String(Date.now()))
        setTimeout(() => window.location.reload(), 0)
      }
    }
  }

  private handleReset = () => {
    this.setState({ error: null })
  }

  private handleReload = () => {
    window.location.reload()
  }

  render() {
    const { error } = this.state
    if (!error) return this.props.children

    if (isChunkLoadError(error)) {
      return (
        <Result
          status="warning"
          title="页面资源已更新"
          subTitle="检测到新版本发布，请刷新页面加载最新资源。"
          extra={
            <Button type="primary" icon={<RefreshCw size={14} />} onClick={this.handleReload}>
              刷新页面
            </Button>
          }
        />
      )
    }

    return (
      <Result
        status="error"
        title="页面出错了"
        subTitle={error.message || '渲染时发生未知错误，请重试'}
        extra={
          <Space>
            <Button type="primary" onClick={this.handleReset}>
              重试
            </Button>
            <Button onClick={this.handleReload}>刷新页面</Button>
          </Space>
        }
      />
    )
  }
}
